import { normalizePhone } from "./textHeuristicsHelpers";

export type PaymentAmbiguous = string | boolean | null | undefined;

function normalizeAmbiguous(v: PaymentAmbiguous): boolean {
  if (v === true) return true;
  if (typeof v === "string" && v.trim().toLowerCase() === "true") return true;
  return false;
}

export const extractBudgetCandidatesFromText = (
  text: string
): { cash?: string; monthly?: string; paymentAmbiguous?: string } => {
  const raw = text || "";
  const t = raw.toLowerCase();

  let cash: string | undefined;
  const cashMatch =
    t.match(/\$\s*([\d,]{4,})/) ||
    t.match(/\b(under|below|around|about|up to|at most)\s*\$?\s*([\d,]{4,})\b/) ||
    t.match(/\b([\d,]{4,})\s*(?:or less|max|maximum)\b/);

  if (cashMatch) {
    const rawNum = (cashMatch[2] || cashMatch[1] || "").replace(/[^\d]/g, "");
    const n = Number(rawNum);
    if (Number.isFinite(n) && n >= 1000) cash = `$${n.toLocaleString()} or less`;
  }

  let monthly: string | undefined;
  const monthlyMatch =
    t.match(/\$\s*(\d{2,5})\s*(?:\/\s*month|\/\s*mo\b|per month|a month|monthly)/) ||
    t.match(/\b(\d{2,5})\s*(?:\/\s*month|\/\s*mo\b|per month|a month|monthly)\b/);

  if (monthlyMatch?.[1]) {
    const n = Number(monthlyMatch[1]);
    if (Number.isFinite(n) && n >= 50) monthly = `$${n.toLocaleString()}/month`;
  }

  let paymentAmbiguous: string | undefined;
  const paymentContext = /\b(rto|rent[-\s]?to[-\s]?own|finance|financing|payment|down)\b/.test(t);

  if (paymentContext && !monthly) {
    const windows: string[] = [];
    const kw = ["payment", "finance", "financing", "rto", "rent to own", "rent-to-own", "down"];
    for (const k of kw) {
      const idx = t.indexOf(k);
      if (idx >= 0) {
        const start = Math.max(0, idx - 60);
        const end = Math.min(t.length, idx + 120);
        windows.push(t.slice(start, end));
      }
    }
    const hay = windows.length ? windows.join(" ") : t;

    const payMatch =
      hay.match(/\$\s*(\d{2,4})\b/) || hay.match(/\b(?:like|about|around|do|at|under|below|stay|keep)\s*\$?\s*(\d{2,4})\b/);

    if (payMatch?.[1]) {
      const n = Number(payMatch[1]);
      if (Number.isFinite(n) && n >= 50 && n <= 3000) paymentAmbiguous = `$${n.toLocaleString()}`;
    }
  }

  return { cash, monthly, paymentAmbiguous };
};

export const consolidateBudget = (
  cash?: string | null,
  monthly?: string | null,
  paymentAmbiguous?: PaymentAmbiguous
): string | null => {
  const c = (cash ?? "").trim() || undefined;
  const m = (monthly ?? "").trim() || undefined;

  const ambiguousFlag = normalizeAmbiguous(paymentAmbiguous);
  const p =
    typeof paymentAmbiguous === "string" && paymentAmbiguous.trim() ? paymentAmbiguous.trim() : undefined;

  const parts = [c, m, p].filter(Boolean) as string[];
  if (!parts.length) return null;

  void ambiguousFlag;

  return parts.join(" | ");
};

export const shouldClearBudgetAsPhoneFragment = (
  budget: string | null,
  phone: string | null,
  transcript?: string
): boolean => {
  const b = (budget || "").trim();
  if (!b) return false;

  const digits = b.replace(/\D/g, "");
  if (!digits) return false;

  const t = (transcript || "").toLowerCase();
  const paymentContext =
    /\b(rto|rent[-\s]?to[-\s]?own|finance|financing|payment|monthly|month|a month|per month|down)\b/.test(t);

  if ((digits.length === 3 || digits.length === 4) && !paymentContext) return true;

  const pDigits = normalizePhone(phone);
  if (pDigits.length >= 10 && digits && pDigits.startsWith(digits)) return true;

  return false;
};
